
import { useState } from 'react';
import moment from 'moment';

import { useDispatch, useSelector } from 'react-redux';
import { get, isEmpty } from 'lodash';
import { FormattedMessage } from 'react-intl';

import { condoFlexibleDatePickerActions, CondoGuestInitialState, condoGuestsActions, condosLocationPickerActions, condoStrictDatesActions, DEFAULT_PERIOD, weeksDatesActions, weeksLocationsActions } from '@share/store/slices';
import { CondoEngines, CondoLocationsEnum, DateSearchTypeEnum, ICondoLocation, StayForEnum } from '@share/common-types';
import { ICondoUnitWeek } from '@common-types';
import { BlueButton } from '@share/components';
import { RootState } from '@share/utils';
import { Routes, USA, VICINITY } from '@share/constants';

import { CondoWeeksDatesModal } from '../condo-week-dates-modal';

import './style.scss';

const NOT_IMAGE_PROPERTY_URL = 'https://travcoding-hotel-resources.s3.us-east-1.amazonaws.com/not_image_property.jpg';
const MAX_PILLS = 4;
const GUEST_VENDOR = '01';

interface IProps {
  condo: ICondoUnitWeek;

  isWeeks: boolean;
  isAccount: boolean;
  isMarketing?: boolean;

  redirect: string;
}

export function CondoWeeksCard(props: IProps) {

  const [displayModal, setDisplayModal] = useState(false);

  const loginStore = useSelector((state: RootState) => state.loginStore);

  const { condo, isWeeks, isAccount, redirect, isMarketing } = props;
  const { image, propertyName, locationName, dates, vendor } = condo;
  const { account } = loginStore;

  const dispatch = useDispatch();

  const handleOpen = () => {
    if (isAccount) {
      dispatch(condoGuestsActions.setState({ ...CondoGuestInitialState }));

      dispatch(condosLocationPickerActions.selectLocationLabel(condo?.locationName));
      dispatch(condosLocationPickerActions.selectLocation({
        id: condo?.locationId?.toString(),
        name: condo?.locationName,

        locationType: CondoLocationsEnum.GenericRegion,
        realType: CondoLocationsEnum.GenericRegion,

        engine: CondoEngines.CONDO
      } as ICondoLocation));

      dispatch(condoFlexibleDatePickerActions.setState({ stayFor: StayForEnum.Week, selectedMonths: [] }));
      dispatch(condoStrictDatesActions.setSearchType(DateSearchTypeEnum.Flexible));

      setTimeout(() => window.open(`/${account?.name}${Routes.Condo}/${condo?.propertyId}${location.search}`, '_blank'), 500);
    } else {
      dispatch(weeksDatesActions.setDateSelected(moment(get(condo?.dates, '[0].checkIn', moment().format('yyyy-MM-DD')), 'yyyy-MM-DD').format('MM/DD/yyyy')));
      dispatch(weeksDatesActions.setPeriodSelected(DEFAULT_PERIOD));
      dispatch(weeksDatesActions.applyWeeksDates());

      dispatch(weeksLocationsActions.setSelectLocationLabel(condo?.locationName));
      dispatch(weeksLocationsActions.setSelectLocation({
        id: condo?.locationId?.toString(),
        name: condo?.locationName,

        locationType: CondoLocationsEnum.GenericRegion,
        realType: CondoLocationsEnum.GenericRegion,

        engine: CondoEngines.WEEKS
      } as ICondoLocation));
      dispatch(weeksLocationsActions.applyLocation());

      setTimeout(() => window.open(`/${account?.name}${Routes.WeeksDetails}/${condo?.propertyId}${location.search}`, '_blank'), 500);
    }
  }

  const renderBookButton = () => {
    if (isMarketing) {
      return null;
    }

    if (redirect) {
      return (
        <a className="condo-weeks-card-new__book-link" href="https://getaways-club.com" target="_blank" rel="noreferrer">Book</a>
      );
    }

    if (isWeeks || isAccount) {
      return (
        <BlueButton onClick={handleOpen}>Book</BlueButton>
      );
    }

    return null;
  };

  const validImage = image && !isEmpty(image);
  const imageUrl = validImage ? image : NOT_IMAGE_PROPERTY_URL;
  const isGuestVendor = vendor === GUEST_VENDOR;

  const firstDate = dates?.[0];
  const datesCount = dates?.length || 0;
  const price = Math.floor(firstDate?.price || 0);
  const lengthLabel = firstDate?.daysOfStay ? `${firstDate.daysOfStay} days` : '';

  const locationDesc = locationName?.replace(VICINITY, '').replace(`, ${USA}`, '').trim() || '';
  const [cityRaw, ...restParts] = locationDesc.split(',').map(s => s.trim());
  const city = cityRaw || '';
  const cityRest = restParts.length ? restParts.join(', ') : '';

  const visiblePills = (dates || []).slice(0, MAX_PILLS);
  const remaining = datesCount - visiblePills.length;

  const bookButton = renderBookButton();
  const hasBookButton = !!bookButton;

  return (
    <div className="condo-weeks-card-wrapper">
      <div className={`condo-weeks-card-new${isGuestVendor ? ' guest-vendor' : ''}`}>
        <div className="condo-weeks-card-new__image-wrap">
          <img className="condo-weeks-card-new__image" src={imageUrl} alt={propertyName} />

          {isGuestVendor ? (
            <div className="condo-weeks-card-new__recommended">
              <p className="condo-weeks-card-new__recommended-title"><FormattedMessage id="recommended.condo" /></p>
            </div>) : null}

          {!validImage && (
            <div className="condo-weeks-card-new__no-image-overlay">
              <p className="condo-weeks-card-new__no-image-text">
                <FormattedMessage
                  id="not.real.image.property"
                  defaultMessage="Not actual photo of the property"
                />
              </p>
            </div>)}

          {firstDate ? (
            <div className="condo-weeks-card-new__price-badge">
              <strong>${price}</strong>
              {lengthLabel ? <span> / {lengthLabel}</span> : null}
            </div>) : null}
        </div>

        <div className="condo-weeks-card-new__body">
          <div className="condo-weeks-card-new__name" title={propertyName}>{propertyName}</div>

          <div className="condo-weeks-card-new__location">
            <span className="condo-weeks-card-new__location-dot">•</span>
            <strong className="condo-weeks-card-new__location-city">{city}</strong>
            {cityRest ? <span className="condo-weeks-card-new__location-rest"> {cityRest}</span> : null}
          </div>

          <div className="condo-weeks-card-new__pills">
            {visiblePills.map((d, i) => {
              const m = moment(d.checkIn, 'yyyy-MM-DD');
              return (
                <div key={`pill_${i}`} className={`condo-weeks-card-new__pill condo-weeks-card-new__pill--clickable${i === 0 ? ' condo-weeks-card-new__pill--active' : ''}`} onClick={() => setDisplayModal(true)}>
                  <div className="condo-weeks-card-new__pill-month">{m.format('MMM').toUpperCase()}</div>
                  <div className="condo-weeks-card-new__pill-day">{m.format('DD')}</div>
                </div>
              );
            })}
            {remaining > 0 ? (
              <div className="condo-weeks-card-new__pill condo-weeks-card-new__pill--more condo-weeks-card-new__pill--clickable" onClick={() => setDisplayModal(true)}>
                <div className="condo-weeks-card-new__pill-plus">+{remaining}</div>
                <div className="condo-weeks-card-new__pill-more">more</div>
              </div>) : null}
          </div>

          <div className={`condo-weeks-card-new__footer${hasBookButton ? '' : ' condo-weeks-card-new__footer--center'}`}>
            <span className="condo-weeks-card-new__dates-available">{datesCount} dates available</span>
            {hasBookButton ? <div className="condo-weeks-card-new__book">{bookButton}</div> : null}
          </div>
        </div>
      </div>

      <CondoWeeksDatesModal condo={condo} visible={displayModal} onCancel={() => setDisplayModal(false)} />
    </div>
  );
}
